import React, { useCallback, useEffect, useRef } from "react";
import { Box, IconButton, Paper, Tooltip } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const PAN_STEP_PX = 80;
const PAN_INTERVAL_MS = 120;

const PAN_BUTTON_SX = {
  width: 36,
  height: 36,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  bgcolor: "#fff",
  color: "#475569",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
  "&:hover": {
    bgcolor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
  "&:active": {
    bgcolor: "#f1f5f9",
  },
};

type PanDirection = "up" | "down" | "left" | "right";

type MapPanControlsProps = {
  onPan: (direction: PanDirection) => void;
};

export function MapPanControls({ onPan }: MapPanControlsProps) {
  const intervalRef = useRef<number | null>(null);

  const stopPanning = useCallback(() => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPanning = useCallback(
    (direction: PanDirection) => {
      stopPanning();
      onPan(direction);
      intervalRef.current = window.setInterval(() => {
        onPan(direction);
      }, PAN_INTERVAL_MS);
    },
    [onPan, stopPanning]
  );

  useEffect(() => stopPanning, [stopPanning]);

  const renderButton = (direction: PanDirection, icon: React.ReactNode, label: string) => (
    <Tooltip key={direction} title={label} placement="top">
      <IconButton
        size="small"
        aria-label={label}
        sx={PAN_BUTTON_SX}
        onMouseDown={(event) => {
          event.preventDefault();
          startPanning(direction);
        }}
        onMouseUp={stopPanning}
        onMouseLeave={stopPanning}
        onTouchStart={(event) => {
          event.preventDefault();
          startPanning(direction);
        }}
        onTouchEnd={stopPanning}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Paper
      elevation={0}
      className="map-screen-overlay-panel"
      sx={{
        position: "absolute",
        bottom: 24,
        right: 16,
        zIndex: 500,
        p: 0.5,
        borderRadius: "12px",
        border: "1px solid #e5e9ef",
        bgcolor: "rgba(255,255,255,0.96)",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 36px)",
          gridTemplateRows: "repeat(3, 36px)",
          gap: 0.35,
          alignItems: "center",
          justifyItems: "center",
        }}
      >
        <Box />
        {renderButton("up", <KeyboardArrowUpIcon sx={{ fontSize: 20 }} />, "Move map up")}
        <Box />
        {renderButton("left", <KeyboardArrowLeftIcon sx={{ fontSize: 20 }} />, "Move map left")}
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: "#cbd5e1",
          }}
        />
        {renderButton(
          "right",
          <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />,
          "Move map right"
        )}
        <Box />
        {renderButton("down", <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />, "Move map down")}
        <Box />
      </Box>
    </Paper>
  );
}

export { PAN_STEP_PX };
export type { PanDirection };
